import React from 'react';

import {
    Grid,
    Collapse,
    Alert
} from '@mui/material';
import {
    changeCustomLicenceCertificate,
    removeCustomLicenceCertifiacate,
    toggleStandardProperty
} from '@utils/metadata';

import { useSelector } from '@hooks';
import { selectSettableMetadataForTenant } from '@actions/metadata';
import { TenantProfileType, METADATA_TYPES } from '@types';

import VehicleTypes from './VehicleTypes';
import Equipment from './Equipment';
import Sustainability from './Sustainability';
import LicencesCertificates from './LicencesCertificates';

type MetadataProps = {
    entity: TenantProfileType;
    onChange: (e: any) => void;
};

const Metadata: React.FC<MetadataProps> = (props) => {
    const { entity, onChange } = props;

    const settableMetadata = useSelector(selectSettableMetadataForTenant());
    const [
        customLicencesCertificates,
        setCustomLicencesCertificates
    ] = React.useState<Array<{ id: string; value: string }>>([]);
    const initializedRef = React.useRef(false);

    React.useEffect(() => {
        if (!initializedRef.current && entity.customLicencesCertificates?.length) {
            setCustomLicencesCertificates(
                entity.customLicencesCertificates
                    .map((cert: string, idx: number) => ({ id: `init-cert-${idx}`, value: cert }))
            );
            initializedRef.current = true;
        }
    }, [entity.customLicencesCertificates]);

    const hasMetadata = entity.vehicleTypes?.length > 0
        || entity.equipment?.length > 0
        || entity.licencesCertificates?.length > 0
        || customLicencesCertificates.some((lic) => lic.value !== '')
        || entity.sustainability?.length > 0;

    const getMetadataArray = (key: string): string[] => {
        if (key === METADATA_TYPES.VEHICLE_TYPES) {
            return entity.vehicleTypes || [];
        }
        if (key === METADATA_TYPES.EQUIPMENT) {
            return entity.equipment || [];
        }
        if (key === METADATA_TYPES.SUSTAINABILITY) {
            return entity.sustainability || [];
        }
        if (key === METADATA_TYPES.LICENCES_CERTIFICATES) {
            return entity.licencesCertificates || [];
        }
        return [];
    };

    const onAllNone = ({ key, array }: { key: string, array: string[] }) => {
        onChange({ ...entity, [key]: array });
    };

    const onClick = ({ key, value }: { key: string; value: string }) => {
        toggleStandardProperty({
            key,
            array: getMetadataArray(key),
            value,
            onChange,
            entity
        });
    };

    const onChangeCustomLicenceCertificate = ({ id, newValue }: { id: string; newValue: string }) => {
        changeCustomLicenceCertificate({
            certId: id,
            newValue,
            customLicencesCertificates,
            setCustomLicencesCertificates,
            entity,
            onChange
        });
    };

    const onRemoveCustomLicenceCertificate = (certId: string) => {
        removeCustomLicenceCertifiacate({
            certId,
            customLicencesCertificates,
            setCustomLicencesCertificates,
            entity,
            onChange
        });
    };

    const addCustomLicenceCertificate = () => {
        setCustomLicencesCertificates([
            ...customLicencesCertificates,
            { id: `custom-cert-${Date.now().toString()}`, value: '' }
        ]);
    };

    return (
        entity.isCarrier ? (
            <>
                <Grid item xs={12}>
                    <Collapse in={!hasMetadata} timeout={1000}>
                        <Alert
                            severity="info"
                            sx={{
                                mt: 2,
                                mb: 2,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: 1
                            }}
                        >
                            Fyll i era era kvalifikationer och kompetenser
                            för att matcha bättre mot uppdrag och synas i sökresultat.
                        </Alert>
                    </Collapse>
                </Grid>
                <Grid item xs={12} sx={{ mt: hasMetadata ? 2 : 0 }}>
                    <Grid container spacing={2}>
                        <VehicleTypes
                            vehicleTypes={entity.vehicleTypes}
                            onAllNone={onAllNone}
                            onClick={onClick}
                            settables={settableMetadata.vehicleTypes}
                        />
                        <Equipment
                            equipment={entity.equipment}
                            onAllNone={onAllNone}
                            onClick={onClick}
                            settables={settableMetadata.equipment}
                        />
                    </Grid>
                </Grid>
                <Grid item xs={12} sx={{ mt: 2 }}>
                    <Grid container spacing={2}>
                        <Sustainability
                            sustainability={entity.sustainability}
                            onAllNone={onAllNone}
                            onClick={onClick}
                            settables={settableMetadata.sustainability}
                        />
                        <LicencesCertificates
                            addCustomLicenceCertificate={addCustomLicenceCertificate}
                            customLicencesCertificates={customLicencesCertificates}
                            licencesCertificates={entity.licencesCertificates}
                            onAllNone={onAllNone}
                            onChangeCustomLicenceCertificate={onChangeCustomLicenceCertificate}
                            onRemoveCustomLicenceCertificate={onRemoveCustomLicenceCertificate}
                            onClick={onClick}
                            settables={settableMetadata.licencesCertificates}
                        />
                    </Grid>
                </Grid>
            </>
        ) : null
    );
};

export default Metadata;
